import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { updateAuctionBidder } from 'api/campaigns';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Bidder } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AnonymizeBidderDialogProps extends AlertDialog.AlertDialogRootProps {
    bidder: Bidder;
}

const AnonymizeBidderDialog = (props: AnonymizeBidderDialogProps) => {
    const queryClient = useQueryClient();
    const { children, bidder, ...rest } = props;
    const { data: campaign } = useCurrentCampaign();
    const campaignId = Number(campaign.id);
    const auctionId = Number(campaign.auction_id);
    const { success, error } = useToast();

    const isAnonymous = bidder.anonymous;
    const anonymousAction = isAnonymous ? 'Reveal' : 'Make anonymous';
    const anonymousButtonText = isAnonymous ? 'Reveal' : 'Anonymize';
    const anonymousTitle = `${anonymousAction} bidder`;
    const anonymousDescription = isAnonymous
        ? 'Actions made by this bidder will be visible to others.'
        : 'Actions made by this bidder will be anonymous and hide their information on the auction page.';

    const updateBidderStatus = isAnonymous ? 'revealed' : 'made anonymous';

    const { mutate: updateBidder } = useMutation({
        mutationFn: ({ id: bidderId, ...rest }: Partial<Omit<Bidder, 'id'>> & { id: number }) =>
            updateAuctionBidder(campaignId, auctionId, bidderId, rest),
        onSuccess: () => {
            success(`Bidder has been ${updateBidderStatus} successfully.`);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
            });
        },
        onError: (err: GivebutterAxiosError) => {
            error(err.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <AlertDialog.Root {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>{anonymousTitle}</AlertDialog.Title>
                        <AlertDialog.Description>{anonymousDescription}</AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() =>
                                updateBidder({
                                    id: bidder.id,
                                    anonymous: !isAnonymous,
                                })
                            }
                        >
                            {anonymousButtonText}
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const AnonymizeBidderDialogTrigger = AlertDialog.Trigger;

const Root = AnonymizeBidderDialog;
const Trigger = AnonymizeBidderDialogTrigger;

export {
    Root,
    Trigger,
    //
    AnonymizeBidderDialog,
    AnonymizeBidderDialogTrigger,
};
